import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { ConditionalLeaderboard, WaitingSpinner, TimerBar, GameEndedScreen, QuestionFeedbackSection, HostPlayingScoreStrip } from '../shared';
import { useHostPlaying } from '../../hooks/useHostPlaying';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { calculateTimeRemaining } from '../../utils/gameUtils';
import { getCloseOrNotBranding } from '../../utils/closeOrNotBranding';
import '../../App.css';
import { useTranslation } from '../../i18n';

interface CloseOrNotQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  numericAnswer?: number;
  unit?: string;
  questionType?: string;
  hint?: string;
  isCloseOrNot?: boolean;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  numericAnswer?: number;
  unit?: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  closenessRanking?: Array<{
    playerId: string;
    playerName: string;
    rank: number;
    answer: number;
    difference: number;
  }>;
}

interface CloseOrNotPlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: any;
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

// Catchy phrases for different closeness levels
const PERFECT_PHRASES = [
  'BULLSEYE! 🎯', 'NAILED IT! 💯', 'SPOT ON!', 'EXACTLY RIGHT!', 'PERFECT HIT!', 'YOU GENIUS!',
  'INCREDIBLE!', 'MIND READER!', 'FLAWLESS!', 'PHENOMENAL!', 'DEAD CENTER!', 'LASER FOCUS!',
  'BANG ON!', 'RIGHT ON THE MONEY!', 'ABSOLUTE PERFECTION!', '10/10!'
];

const VERY_CLOSE_PHRASES = [
  'SO CLOSE! 🔥', 'ALMOST PERFECT!', 'NEARLY THERE!', 'JUST A HAIR OFF!', 'SUPER CLOSE!',
  'EXCELLENT GUESS!', 'RIGHT ON TARGET!', 'IMPRESSIVE!', 'FANTASTIC!', 'BRILLIANT!',
  'THAT WAS TIGHT!', 'ALMOST HAD IT!', 'CLOSE CALL!', 'NEAR MISS!', 'SHARP GUESS!'
];

const CLOSE_PHRASES = [
  'PRETTY CLOSE! 👍', 'GOOD GUESS!', 'NOT BAD AT ALL!', 'SOLID EFFORT!', 'NICE TRY!',
  'IN THE BALLPARK!', 'WARM!', 'GETTING THERE!', 'RESPECTABLE!', 'DECENT SHOT!',
  'ON THE BOARD!', 'NOT FAR OFF!', 'CLOSER THAN MOST!', 'SOLID RANGE!', 'YOU\'RE WARMING UP!'
];

const NOT_BAD_PHRASES = [
  'COULD BE WORSE 😅', 'NOT TOO FAR!', 'KEEP TRYING!', 'REASONABLE GUESS!', 'FAIR ATTEMPT!',
  'HALFWAY THERE!', 'ROOM FOR IMPROVEMENT!', 'WORTH A SHOT!', 'NICE EFFORT!', 'LUKE WARM!',
  'CLOSE ENOUGH-ish!', 'A BIT OFF BUT OK!', 'NOT TERRIBLE!', 'YOU\'RE IN RANGE!', 'ALMOST WARM!'
];

const FAR_PHRASES = [
  'GETTING COLDER 😬', 'A BIT OFF!', 'WIDE OF THE MARK!', 'NEEDS WORK!', 'KEEP GUESSING!',
  'NOT QUITE!', 'TRY AGAIN!', 'MISSED IT!', 'OFF TARGET!', 'WORK IN PROGRESS!',
  'COLD GUESS!', 'WAY OUT THERE!', 'NOT THE ONE!', 'SWING WIDER!', 'BACK TO THE DRAWING BOARD!'
];

const WAY_OFF_PHRASES = [
  'WAY OFF! 😵', 'TOTALLY WRONG!', 'NOT EVEN CLOSE!', 'YIKES!', 'OOPS!',
  'BETTER LUCK NEXT TIME!', 'WILD GUESS?', 'SWING AND A MISS!', 'ICE COLD!', 'GALAXY BRAIN? 🌌',
  'MILES AWAY!', 'OUT IN LEFT FIELD!', 'COLD AS ICE!', 'NOPE CITY!', 'SPACE CADET!'
];

const pickRandom = (arr: string[]): string => arr[Math.floor(Math.random() * arr.length)];

// Theme colors for Close or Not
const THEME = {
  primary: '#F97316', // Orange
  primaryLight: 'rgba(249, 115, 22, 0.15)',
  primaryBorder: 'rgba(249, 115, 22, 0.3)',
  accent: '#34D399', // Green
  accentLight: 'rgba(52, 211, 153, 0.15)',
  accentBorder: 'rgba(52, 211, 153, 0.3)',
};

const CloseOrNotPlayerDisplay: React.FC<CloseOrNotPlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const branding = getCloseOrNotBranding(roomState.gameType);
  const hostIsPlaying = useHostPlaying(roomState);
  const [currentQuestion, setCurrentQuestion] = useState<CloseOrNotQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<{
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
    totalQuestions: number;
    averageScore: number;
  } | null>(null);
  const [answer, setAnswer] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [closenessRank, setClosenessRank] = useState<number | null>(null);
  const [correctNumericAnswer, setCorrectNumericAnswer] = useState<number | null>(null);
  const [playerNumericAnswer, setPlayerNumericAnswer] = useState<number | null>(null);
  const [difference, setDifference] = useState<number | null>(null);
  const [myScore, setMyScore] = useState(0);
  const [showInterestingFact, setShowInterestingFact] = useState(false);
  const [closenessFeedback, setClosenessFeedback] = useState<{ emoji: string; text: string; color: string; isGood: boolean; isExact: boolean } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const answerSubmittedTimeRef = useRef<number | null>(null);
  const previousQuestionIdRef = useRef<string | undefined>(
    roomState.currentQuestion?.questionId || roomState.currentQuestion?.id || undefined
  );
  const currentQuestionIdRef = useRef<string | undefined>(
    roomState.currentQuestion?.questionId || roomState.currentQuestion?.id || undefined
  );

  const gameEndData = useGameEndScreenData(roomState);

  // Helper to reset question state
  const resetQuestion = () => {
    setAnswer('');
    setAnswerSubmitted(false);
    setPointsEarned(0);
    setClosenessRank(null);
    setCorrectNumericAnswer(null);
    setPlayerNumericAnswer(null);
    setDifference(null);
    setShowInterestingFact(false);
    setClosenessFeedback(null);
    isSubmittingRef.current = false;
    answerSubmittedTimeRef.current = null;
    // Focus input for quick typing
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Get closeness feedback based on difference
  const calculateClosenessFeedback = (diff: number | null, correct: number | null): { emoji: string; text: string; color: string; isGood: boolean; isExact: boolean } => {
    if (diff === null || correct === null) {
      return { emoji: '🤔', text: 'Invalid answer', color: '#EF4444', isGood: false, isExact: false };
    }

    const percentDiff = correct !== 0 ? (diff / Math.abs(correct)) * 100 : diff;
    const isExact = diff === 0 || (correct !== 0 && percentDiff < 0.001); // Handle float precision

    if (isExact) {
      return { emoji: '🎯', text: pickRandom(PERFECT_PHRASES), color: '#22C55E', isGood: true, isExact: true };
    } else if (percentDiff <= 1) {
      return { emoji: '🔥', text: pickRandom(VERY_CLOSE_PHRASES), color: '#22C55E', isGood: true, isExact: false };
    } else if (percentDiff <= 5) {
      return { emoji: '✨', text: pickRandom(VERY_CLOSE_PHRASES), color: '#22C55E', isGood: true, isExact: false };
    } else if (percentDiff <= 10) {
      return { emoji: '👍', text: pickRandom(CLOSE_PHRASES), color: '#84CC16', isGood: true, isExact: false };
    } else if (percentDiff <= 25) {
      return { emoji: '😅', text: pickRandom(NOT_BAD_PHRASES), color: '#F59E0B', isGood: false, isExact: false };
    } else if (percentDiff <= 50) {
      return { emoji: '😬', text: pickRandom(FAR_PHRASES), color: '#F97316', isGood: false, isExact: false };
    } else {
      return { emoji: '😵', text: pickRandom(WAY_OFF_PHRASES), color: '#EF4444', isGood: false, isExact: false };
    }
  };

  // When server says game is finished (e.g. reconnection, or room-state before game-ended), show game end screen
  const roomGameState = roomState.gameState;
  useEffect(() => {
    if (roomGameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gameEndData is derived from roomState
  }, [roomGameState, gameEndData]);

  // Sync score from roomState
  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const myPlayer = roomState.players.find(p => p.id === myPlayerId);
      if (myPlayer) {
        setMyScore(myPlayer.score);
      }
    }
  }, [roomState.players, myPlayerId]);

  // Update when roomState.currentQuestion changes
  useEffect(() => {
    if (roomState.currentQuestion) {
      const newQuestionId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
      const isNewQuestion = newQuestionId !== previousQuestionIdRef.current;
      const questionStartTime = (roomState as any).questionStartTime;
      const serverTimeRemaining = calculateTimeRemaining(roomState.currentQuestion, questionStartTime);
      if (isNewQuestion || !currentQuestion) {
        previousQuestionIdRef.current = newQuestionId;
        currentQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(roomState.currentQuestion);
        setTimeRemaining(serverTimeRemaining);
        if (isNewQuestion) {
          resetQuestion();
        }
      } else {
        setTimeRemaining(serverTimeRemaining);
      }
    }
  }, [roomState.currentQuestion, (roomState as any).questionStartTime, currentQuestion]);

  useEffect(() => {
    const handleQuestion = (data: CloseOrNotQuestion) => {
      console.log('CloseOrNotPlayerDisplay received question:', data);
      const newQuestionId = data.questionId || data.id;

      if (newQuestionId !== previousQuestionIdRef.current) {
        previousQuestionIdRef.current = newQuestionId;
        currentQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(data);
        setTimeRemaining((data as any).timeRemaining ?? data.timeLimit);
        resetQuestion();
      }
    };

    const handleQuestionResult = (data: QuestionResult) => {
      console.log('Question result:', data);

      // If current player was the last one to answer, show flip immediately
      if (answerSubmittedTimeRef.current) {
        const timeSinceSubmit = Date.now() - answerSubmittedTimeRef.current;
        if (timeSinceSubmit < 1500) {
          setTimeout(() => {
            setShowInterestingFact(true);
          }, 500);
        }
      }
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: {
        mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
        wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
        totalQuestions: number;
        averageScore: number;
      };
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      resetQuestion();
    };

    const handleAnswerReceived = (data: {
      isCorrect: boolean;
      points: number;
      responseTime: number;
      questionId?: string;
      closenessRank?: number | null;
      numericAnswer?: number;
      playerAnswer?: number;
      difference?: number;
      correctAnswer?: string;
    }) => {
      const eventQuestionId = data.questionId ?? currentQuestionIdRef.current;
      if (currentQuestionIdRef.current && eventQuestionId !== currentQuestionIdRef.current) {
        return;
      }
      console.log('Answer received:', data);
      isSubmittingRef.current = false;
      setAnswerSubmitted(true);
      setPointsEarned(data.points);
      setClosenessRank(data.closenessRank || null);
      setCorrectNumericAnswer(data.numericAnswer || null);
      setPlayerNumericAnswer(data.playerAnswer || null);
      setDifference(data.difference !== undefined ? data.difference : null);
      // Score is synced from room-state; don't add data.points to avoid wrong + points when events reorder

      // Calculate and set feedback
      const feedback = calculateClosenessFeedback(
        data.difference !== undefined ? data.difference : null,
        data.numericAnswer || null
      );
      setClosenessFeedback(feedback);

      // Track submission time for last-player detection
      answerSubmittedTimeRef.current = Date.now();

      // Show interesting fact after a short delay
      const answeredQuestionId = currentQuestionIdRef.current;
      setShowInterestingFact(false);
      setTimeout(() => {
        if (currentQuestionIdRef.current === answeredQuestionId) {
          setShowInterestingFact(true);
        }
      }, 1500);
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket, myPlayerId]);

  // Timer countdown
  useEffect(() => {
    if (currentQuestion && timeRemaining > 0 && !answerSubmitted) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 100) return 0;
          return prev - 100;
        });
      }, 100);

      return () => clearInterval(timer);
    }
  }, [currentQuestion, timeRemaining, answerSubmitted]);

  const handleSubmitAnswer = () => {
    if (!answer.trim() || !currentQuestion || answerSubmitted || isSubmittingRef.current) {
      return;
    }

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      console.error('Question ID not found');
      return;
    }

    isSubmittingRef.current = true;
    setAnswerSubmitted(true);

    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: questionId,
      answer: answer.trim(),
      timestamp: Date.now(),
    });
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };

  // Get rank emoji and text
  const getRankText = (): string | undefined => {
    if (closenessRank === null) return undefined;
    switch (closenessRank) {
      case 1: return '🥇 Closest!';
      case 2: return '🥈 2nd Closest';
      case 3: return '🥉 3rd Closest';
      default: return `#${closenessRank} Closest`;
    }
  };

  // Game ended screen
  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        resetQuestion();
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    const displayLeaderboard = finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? []);
    const displayStatistics = gameStatistics ?? gameEndData?.gameStatistics ?? null;
    return (
      <GameEndedScreen
        gameIcon="🎯"
        finalLeaderboard={displayLeaderboard}
        myPlayerId={myPlayerId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        showYouSuffix
        statistics={displayStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  // Loading state
  if (!currentQuestion) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">{branding.icon} {branding.title}</h1>
          <p className="subtitle">{t('gameplay.closeOrNot.getReady')}</p>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid rgba(248, 250, 252, 0.3)',
              borderTop: '4px solid #F8FAFC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    }
  };

  const sortedPlayers = [...roomState.players].sort((a, b) => b.score - a.score);

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.home,
          onClick: handleGoHome,
          tooltip: t('gameplay.leaveGame')
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage')
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <span className="header-game-name" style={{ fontSize: '1rem' }}>{branding.icon} {branding.title}</span>
            <span style={{
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: THEME.accent,
              padding: '0.3rem 0.6rem',
              backgroundColor: THEME.accentLight,
              borderRadius: '6px',
              border: `2px solid ${THEME.accent}`,
            }}>
              {roomState.roomName}
            </span>
          </div>
        }
      />
      <div className="container" style={{ paddingTop: '3rem' }}>
        <HostPlayingScoreStrip
          hostIsPlaying={hostIsPlaying}
          players={sortedPlayers}
          myPlayerId={myPlayerId}
          pointsThisQuestion={answerSubmitted ? pointsEarned : null}
          showQuestionPointsLine={answerSubmitted}
        />
        {!answerSubmitted ? (
          // Question view - ready to answer
          <>
            {/* Conditional Leaderboard - only shown when host is playing */}
            <ConditionalLeaderboard
              hostIsPlaying={hostIsPlaying}
              players={sortedPlayers}
              myPlayerId={myPlayerId}
            />

            <TimerBar
              timeRemaining={timeRemaining}
              timeLimit={currentQuestion.timeLimit}
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
              showTimeCounter={false}
            />

            {/* Conditional Question Display - only shown when host is playing */}
            {hostIsPlaying && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1.5rem',
                backgroundColor: 'rgba(249, 115, 22, 0.12)',
                borderRadius: '15px',
                border: `2px solid ${THEME.primaryBorder}`,
              }}>
                <h2 style={{
                  fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                  color: 'var(--text-primary)',
                  textAlign: 'center',
                  margin: 0,
                  lineHeight: 1.4,
                }}>
                  {currentQuestion.question}
                </h2>
                {currentQuestion.hint && (
                  <p style={{
                    fontSize: '0.9rem',
                    color: 'var(--text-secondary)',
                    textAlign: 'center',
                    marginTop: '0.75rem',
                    marginBottom: 0,
                  }}>
                    💡 Hint: {currentQuestion.hint}
                  </p>
                )}
                {currentQuestion.unit && (
                  <p style={{
                    fontSize: '1.15rem',
                    color: THEME.primary,
                    textAlign: 'center',
                    marginTop: '0.5rem',
                    marginBottom: 0,
                  }}>
                    Answer in: {currentQuestion.unit}
                  </p>
                )}
              </div>
            )}

            {/* Answer input - allow any characters */}
            <div style={{ marginBottom: '1.5rem' }}>
              <input
                ref={inputRef}
                type="text"
                inputMode="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('gameplay.typeYourAnswer')}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '1.2rem',
                  fontSize: '2rem',
                  fontFamily: 'monospace',
                  textAlign: 'center',
                  backgroundColor: 'rgba(248, 250, 252, 0.1)',
                  border: `3px solid ${THEME.primaryBorder}`,
                  borderRadius: '15px',
                  color: '#fff',
                  outline: 'none',
                }}
              />
            </div>

            {/* Submit button */}
            <button
              onClick={handleSubmitAnswer}
              disabled={!answer.trim()}
              className="button player-answer-submit-button"
              style={{
                width: '100%',
                backgroundColor: answer.trim() ? THEME.primary : undefined,
              }}
            >
              {t('gameplay.submitAnswer')}
            </button>
          </>
        ) : (
          // Result view - answer submitted (like other games)
          <div style={{
            width: '100%',
            minHeight: 'calc(100dvh - 200px)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          }}>
            {/* Conditional Leaderboard after answer - only shown when host is playing */}
            <ConditionalLeaderboard
              hostIsPlaying={hostIsPlaying}
              players={sortedPlayers}
              myPlayerId={myPlayerId}
            />

            {/* Flip card - simple correct/close message like other games */}
            {closenessFeedback && (() => {
              const isExact = closenessFeedback.isExact;
              const isClose = closenessFeedback.isGood && !closenessFeedback.isExact;
              const wrapperBg = 'rgba(255, 255, 255, 0.9)';
              const wrapperBorder = isExact ? '#34D399' : isClose ? '#F97316' : '#EF4444';
              const wrapperAccent = isExact ? '#34D399' : isClose ? '#F97316' : '#EF4444';
              const didYouKnowFlipped = !!(showInterestingFact && currentQuestion?.interesting);
              return (
              <div style={{
                perspective: '1000px',
                marginTop: '1rem',
              }}>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  minHeight: currentQuestion?.interesting ? '120px' : 'auto',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: didYouKnowFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                }}>
                  {/* Front - Simple Correct/Close Message */}
                  <div style={{
                    position: didYouKnowFlipped ? 'absolute' : 'relative',
                    top: didYouKnowFlipped ? 0 : undefined,
                    left: didYouKnowFlipped ? 0 : undefined,
                    right: didYouKnowFlipped ? 0 : undefined,
                    width: '100%',
                    padding: '2rem 1.5rem',
                    backgroundColor: wrapperBg,
                    border: `2px solid ${wrapperBorder}`,
                    borderRadius: '15px',
                    textAlign: 'center',
                    color: 'var(--text-primary)',
                    boxShadow: 'var(--shadow-md)',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    boxSizing: 'border-box',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                    }}>
                      <span style={{ fontSize: '2rem', fontWeight: 'bold', color: wrapperAccent }}>
                        {isExact ? '✓' : isClose ? '~' : '✗'}
                      </span>
                      <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                        {closenessFeedback.isExact ? 'Correct!' : closenessFeedback.isGood ? 'Close!' : 'Not Close'}
                      </span>
                    </div>
                  </div>

                  {/* Back - Did you know */}
                  {currentQuestion?.interesting && (
                    <div style={{
                      position: didYouKnowFlipped ? 'relative' : 'absolute',
                      top: didYouKnowFlipped ? undefined : 0,
                      left: didYouKnowFlipped ? undefined : 0,
                      right: didYouKnowFlipped ? undefined : 0,
                      width: '100%',
                      padding: '2rem 1.5rem',
                      backgroundColor: wrapperBg,
                      border: `2px solid ${wrapperBorder}`,
                      borderRadius: '15px',
                      textAlign: 'center',
                      color: 'var(--text-primary)',
                      boxShadow: 'var(--shadow-md)',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      boxSizing: 'border-box',
                    }}>
                      <p style={{
                        fontSize: '1.1rem',
                        color: wrapperAccent,
                        fontWeight: 'bold',
                        margin: '0 0 0.8rem 0',
                      }}>
                        💡 Did you know?
                      </p>
                      <p style={{
                        fontSize: '1rem',
                        color: 'var(--text-primary)',
                        lineHeight: '1.6',
                        margin: 0,
                      }}>
                        {currentQuestion.interesting}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ); })()}

            {/* Detailed breakdown - always visible below flip card */}
            {closenessFeedback && (
              <div style={{
                padding: '1.5rem',
                backgroundColor: THEME.primaryLight,
                borderRadius: '15px',
                border: `2px solid ${THEME.primaryBorder}`,
                textAlign: 'center',
                marginTop: '3.5rem',
                marginBottom: '1rem',
              }}>
                {/* Big emoji and phrase */}
                <div style={{ fontSize: '3rem', marginBottom: '0.25rem' }}>
                  {closenessFeedback.emoji}
                </div>
                <div style={{
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  color: closenessFeedback.color,
                  marginBottom: '1rem',
                }}>
                  {closenessFeedback.text}
                </div>

                {/* Answer comparison */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '2rem',
                  marginBottom: '0.75rem',
                  flexWrap: 'wrap',
                }}>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.2rem' }}>
                      Your Answer
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#fff' }}>
                      {playerNumericAnswer !== null ? playerNumericAnswer.toLocaleString() : answer}
                      {currentQuestion.unit && <span style={{ fontSize: '0.75rem', marginLeft: '0.2rem' }}>{currentQuestion.unit}</span>}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.2rem' }}>
                      {t('gameplay.correctAnswerLabel')}
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: THEME.accent }}>
                      {correctNumericAnswer !== null ? correctNumericAnswer.toLocaleString() : '?'}
                      {currentQuestion.unit && <span style={{ fontSize: '0.75rem', marginLeft: '0.2rem' }}>{currentQuestion.unit}</span>}
                    </div>
                  </div>
                </div>

                {/* Difference display */}
                {difference !== null && difference > 0 && (
                  <div style={{
                    fontSize: '0.85rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '0.75rem',
                  }}>
                    You were off by: <span style={{ fontWeight: 'bold', color: THEME.primary }}>
                      {difference.toLocaleString()}
                    </span>
                    {currentQuestion.unit && ` ${currentQuestion.unit}`}
                  </div>
                )}

                {/* Rank and points */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  marginTop: '0.5rem',
                }}>
                  {getRankText() && (
                    <div style={{
                      padding: '0.4rem 0.8rem',
                      backgroundColor: THEME.primaryLight,
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      color: 'var(--text-primary)',
                    boxShadow: 'var(--shadow-md)',
                      border: `1px solid ${THEME.primary}`,
                    }}>
                      {getRankText()}
                    </div>
                  )}
                  <div style={{
                    padding: '0.4rem 0.8rem',
                    backgroundColor: pointsEarned > 0 ? THEME.accentLight : 'rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    color: pointsEarned > 0 ? THEME.accent : '#EF4444',
                  }}>
                    +{pointsEarned} pts
                  </div>
                </div>
              </div>
            )}

            <WaitingSpinner isLoading={false} />
            {answerSubmitted && closenessFeedback && currentQuestion && (
              <div style={{ marginTop: 'auto', width: '100%' }}>
                <QuestionFeedbackSection
                  questionId={currentQuestion.questionId || currentQuestion.id || ''}
                  roomName={roomState.roomName}
                  gameType={roomState.gameType}
                  answerSubmitted={answerSubmitted}
                  showFeedback={true}
                  onSubmitFeedback={(rating, correct, questionText, correctAnswerVal) => {
                    if ((rating !== undefined || correct !== undefined) && socket) {
                      socket.emit('question-feedback', {
                        roomName: roomState.roomName,
                        questionId: currentQuestion.questionId || currentQuestion.id,
                        gameType: roomState.gameType,
                        rating: rating ?? undefined,
                        correct: correct ?? undefined,
                        questionText: questionText ?? currentQuestion.question,
                        correctAnswer: correctAnswerVal ?? (correctNumericAnswer !== null ? String(correctNumericAnswer) : undefined),
                      });
                    }
                  }}
                  questionText={currentQuestion.question}
                  correctAnswer={correctNumericAnswer !== null ? String(correctNumericAnswer) : undefined}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CloseOrNotPlayerDisplay;
